import { userInfos } from "@/state/api/userState";
import { featuresMap, featuresLoadedAt } from "@/state/api/featuresState";

/**
 * Check if the current user has the Root role.
 */
function isRootUser(): boolean {
	if (!userInfos.value?.roles) return false;
	return userInfos.value?.roles?.some(
		(role: any) => role.name === "Root" || role.id === 4
	);
}

/**
 * Check a registry feature key in dot.notation (`feature.planning.embarkations`,
 * `module.planning`, …). Single source of truth: `featuresMap`, hydratée par
 * `/features/me` au login et tenue à jour via broadcast Reverb.
 *
 * Sémantique alignée sur `featureGuard` afin que le sidebar et le router
 * soient toujours d'accord :
 *   - Root bypass (Root ne se verrouille pas hors de son propre back-office).
 *   - Map jamais chargée → permissif (les écrans d'auth doivent rester accessibles).
 *   - Clé absente de la map → permissif (features default-enabled).
 *   - Clé présente → on respecte le `enabled` résolu côté serveur.
 *
 * NB. Le système legacy `ENABLE_*` a été retiré : plus aucun fallback vers
 * l'ancienne map `enterpriseFeatures`. Toute désactivation de module doit
 * passer par EnterpriseFeatureManagement (clé `module.*` ou `feature.*`).
 */
export function isFeatureKeyOn(featureKey?: string | null): boolean {
	if (!featureKey) return true;
	if (isRootUser()) return true;

	if (featuresLoadedAt.value > 0 && Object.keys(featuresMap.value).length > 0) {
		const entry = featuresMap.value[featureKey];
		if (entry !== undefined) {
			return !!entry.enabled;
		}
		// Clé absente de la map → default-enabled (cf. cascade serveur).
		return true;
	}

	// Map pas encore chargée → permissif (la nav initiale en pré-hydratation
	// ne doit pas masquer le menu ; le watcher du featureGuard repasse une
	// seconde fois dès que la map arrive).
	return true;
}
